import i18n, { TFunction } from "i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// en-US is the typed source of truth; the other locales are translator-maintained
// but still typed .ts files (see ./locales/*.ts) so a bad/misspelled key is a compile error.
// Generated JSON (for the .NET backend and the runtime HTTP fallback) is derived from
// these by generate-json.ts, auto-regenerated and staged by the pre-commit hook
// (.husky/wrappers/generate-i18n-json.ps1) whenever a locale .ts file changes.
// It is committed (the JSON needs to be in the repo since it's what the .NET backend serves), but should never be hand-edited - edit the .ts files instead.
import { en as enUS } from "./locales/en-US";
import { frCA } from "./locales/fr-CA";
import { esMX } from "./locales/es-MX";
import { hiIN } from "./locales/hi-IN";
import { paIN } from "./locales/pa-IN";
import { ruRU } from "./locales/ru-RU";
import { zhCN } from "./locales/zh-CN";

// Registers the shape of `enUS` as i18next's key set, so every `t()`/`useTranslation()`
// call site across the codebase gets autocomplete and a compile error on a bad key.
declare module "i18next" {
  interface CustomTypeOptions {
    defaultNS: "strings";
    resources: {
      strings: typeof enUS;
    };
  }
}

// This defines what the base translations file name will be (`.json` will be appended)
// The actual filename template is below where `loadPath` is defined.
const baseTranslationFileName = "strings";

const modifyStringForTesting = (str: string) => {
  return (
    ">" +
    str
      .split("")
      .map((char, index) => (index % 2 === 0 ? char.toLowerCase() : char.toUpperCase()))
      .join("") +
    "<"
  );
};

const _additionalNamespaces: string[] = [];

export async function initI18n(...additionalNamespaces: string[]): Promise<TFunction> {
  const urlParams = new URLSearchParams(window.location.search);
  const forceLocaleCode = urlParams.get("lc") || undefined;

  _additionalNamespaces.push(...additionalNamespaces);

  const i18nPromise = i18n
    .use(Backend)
    .use(LanguageDetector)
    .use({
      type: "postProcessor",
      name: "modifyStringForTesting",
      process: modifyStringForTesting,
    })
    .use(initReactI18next)
    .init({
      partialBundledLanguages: !!additionalNamespaces.length,
      backend: {
        loadPath: "/locales/{{lng}}/{{ns}}.json",
      },
      lng: forceLocaleCode,
      load: "currentOnly",
      fallbackLng: "en-US",
      ns: [baseTranslationFileName],
      defaultNS: "strings",
      nsSeparator: false,
      keySeparator: ".",
      debug: true,
      resources: {
        "en-US": {
          strings: enUS,
        },
        "fr-CA": {
          strings: frCA,
        },
        "es-MX": {
          strings: esMX,
        },
        "hi-IN": {
          strings: hiIN,
        },
        "pa-IN": {
          strings: paIN,
        },
        "ru-RU": {
          strings: ruRU,
        },
        "zh-CN": {
          strings: zhCN,
        },
      },
      interpolation: {
        escapeValue: false,
      },
      // postProcess: ["modifyStringForTesting"], // Uncomment to debug if all strings have translations
      react: {
        useSuspense: true,
      },
    });

  await i18nPromise;
  if (forceLocaleCode) {
    await loadLanguage(forceLocaleCode);
  }
  return i18nPromise;
}

//
// Call this from the UI to explicitly load a language.
// The `additionalNamespaces` provision allows us to include
// special strings tables that only exist for some clients
// or e.g. separate general UI strings from config strings.
//
export async function loadLanguage(localeCode: string): Promise<void> {
  const namespaces = [baseTranslationFileName, ..._additionalNamespaces];
  await i18n.changeLanguage(localeCode);
  await i18n.loadNamespaces(namespaces);
}

//
// Use `getCollator()` for string comparisons
//
let collators: Record<"search" | "sort" | "sort-numeric", Intl.Collator>;

function assignCollators(localeCode: string) {
  collators = {
    // use 'sort-numeric' for numbers that are in strings (e.g. "Lane 3" < "Lane 10")
    "sort-numeric": new Intl.Collator(localeCode, {
      sensitivity: "accent",
      usage: "sort",
      numeric: true,
    }),

    // Use 'sort' and 'search' for general strings
    sort: new Intl.Collator(localeCode, { sensitivity: "accent", usage: "sort" }),
    search: new Intl.Collator(localeCode, { sensitivity: "accent", usage: "search" }),
  };
}

assignCollators(i18n.language);

i18n.on("languageChanged", assignCollators);

export function getCollator(usage: keyof typeof collators) {
  return collators[usage];
}

// TODO: improve this
export function localeAwareIncludes(
  sourceContent: string | null,
  searchValue: string,
): boolean {
  if (!sourceContent) return false;
  const n = searchValue.length;
  for (let i = 0; i <= sourceContent.length - n; i++) {
    const substring = sourceContent.substring(i, i + n);
    if (collators.search.compare(substring, searchValue) === 0) return true;
  }
  return false;
}

/**
 * Casts a typed `t()` to accept any runtime string key.
 *
 * Use only when keys come from external data (parameter definitions, BRE
 * messages, route params). Prefer typing the prop where the caller knows the key.
 */
export function tDynamic(
  t: TFunction,
): (key: string, options?: Record<string, unknown>) => string {
  return t as unknown as (key: string, options?: Record<string, unknown>) => string;
}
